import {
  getSnapshot,
  applySnapshot,
  types as t,
  onSnapshot,
  getEnv,
  SnapshotOrInstance,
  SnapshotOut,
  SnapshotIn,
  Instance, 
  IJsonPatch,
  IAnyStateTreeNode,
  splitJsonPath,
  isModelType,
  isStateTreeNode,
  joinJsonPath,
  getType,
  flow,
  getPath
} from '@invisr/mobx-state-tree'
import { runInAction, configure, toJS, transaction } from 'mobx'
import localStore from 'store'
import { AppInfo, AppDetails, AppItem, resolve } from './models/app'
import { withoutPatch, withoutPatchFlow } from '../utils/without-patch'
import { v4 as uuid } from 'uuid'
import { nanoid as shortid, customAlphabet } from 'nanoid'
import crudActions from './models/crud-actions'
import debounce from 'lodash/debounce'
import cloneDeep from 'lodash/cloneDeep'
import merge from 'lodash/merge'
import _set from 'lodash/set'
import errorToast from '../utils/error-toast'
import cleanDeep from 'clean-deep'
import fetch from '../utils/fetch'
import * as perf from 'marky'
import { traverseConfigFields } from '../utils/datasource-config-utils'
import { getDataSourceDbKeyPrefix } from '../utils/datasourceUtils'
import { getDsSecretFields } from '../utils/datasource-secrets-utils'
import Site from './models/site'
import type Component from './models/component'
import type AssignmentVersion from './models/assignment-version'
import type DataSource from './models/datasource'
import type { MappingItem } from './models/mapping'
import transformPatches from '../utils/transformPatches'

export const changeAppKey = (key: string) => key.replace(/^\/app\/([^\/]+)\/([^\/]+)\//, '/app:$1:$2/')
const alphaNumId = customAlphabet('1234567890qwertyuiopasdfghjklzxcvbnm', 16)
interface IComponent extends Instance<typeof Component> {}

configure({
  enforceActions: 'observed'
//  computedRequiresReaction: true,
//  reactionRequiresObservable: true,
//  observableRequiresReaction: true,
//  disableErrorBoundaries: true
})

export const Store = t
  .model('Store', {
    settings: t.frozen<Record<string, any>>({}),
    currentAssignmentVersions: t.frozen({}),
    apps: t.map(AppInfo),
    app: t.map(AppDetails),
    sites: t.map(Site),
    assignmentVersion: t.maybe(t.frozen({}))
  })
  .volatile(self => ({
    currentAppKey: 'default',
    fetchedApps: {},
    saveInProgress: false,
    isLoading: false
  }))
  .actions(self => ({
    processApp (key: string, snap: SnapshotOrInstance<typeof AppDetails>) {
      snap.key = key
      applySnapshot(self.app, { ...getSnapshot(self.app), [key]: { ...(self.app.has(key) ? (getSnapshot(self.app.get(key)) as any) : {}), ...snap } })
    },
    setIsLoading (s: boolean) {
      self.isLoading = s
    },
    fetchSites: async () => {
      const result = await fetch('/admin/allSites').then(r => r.json())
      withoutPatch(self, () => applySnapshot(self.sites, result.sites))
      return result;
    },
  }))
  .actions(self => {
    const _fetchApp = flow(function * _fetchApp(key: string, subkeys = []) {
      let results: SnapshotOrInstance<typeof Store>;

      perf.mark('fetch-app')
      if(subkeys.length) {
	      results = yield fetch(`/admin/partialApp/${key}?keys=${subkeys.join(',')}`).then(r => r.json())
      } else {
	      results = yield fetch(`/admin/fullApp/${key}`).then(r => r.json())
      }
      perf.stop('fetch-app')

      return withoutPatch(self, () => {
        const env = getEnv(self)
        const app = results.app[key]
        env.cache = { mapping: app.mapping }
        app.mapping = {}
        perf.mark('load-app')
        self.processApp(key, results.app[key])
        perf.stop('load-app')
        self.setIsLoading(false)
        
        return self.app.get(key)
      })
    })
    return {
      setSaveProgress: (val: boolean) => self.saveInProgress = val,
      fetchApp: (key, subkeys) => {
        if (self.fetchedApps[key]) return self.app.get(key)
        self.setIsLoading(true)
        if(!subkeys?.length) self.fetchedApps[key] = true
        return _fetchApp(key, subkeys)
      },
      processApp (key, snap) {
        snap.key = key
        applySnapshot(self.app, { ...getSnapshot(self.app), [key]: { ...(self.app.has(key) ? (getSnapshot(self.app.get(key)) as any) : {}), ...snap } })
      }
    }
  })
  .actions(self => ({
    setCurrentAppKey (key, thingsToFetch) {
      const app = Array.from(self.apps.values()).find(a => a.name === key)
      self.currentAppKey = app ? app.id : key
      if(typeof window === 'undefined') return;
      return self.fetchApp(self.currentAppKey, thingsToFetch)
    }
  }))
  .views(self => ({
    get currentApp () {
      return self.app.get(self.currentAppKey) as Instance<typeof AppDetails>
    }
  }))
  .views(self => ({
    get layouts () {
      return self.currentApp?.layouts || new Map()
    },
    get pages () {
      return self.currentApp?.pages || new Map()
    },
    get navigation () {
      return self.currentApp?.navigation || new Map()
    },
    get assignments () {
      return self.currentApp?.assignments || new Map()
    },
    get assignmentVersions () {
      return self.currentApp?.assignmentVersions || new Map()
    },
    get dataSources () {
      return self.currentApp?.dataSources || new Map()
    },
    get components () {
      return self.currentApp?.components || new Map()
    },
    get themes () {
      return self.currentApp?.themes || new Map()
    },
    get mapping () {
      return self.currentApp?.mapping || new Map()
    },
    get workflowConfigs () {
      return self.currentApp?.workflowConfigs || new Map()
    },
    get assignmentVersionId () {
      return self.assignmentVersion?.['id']
    }
  }))
  .volatile(self => ({
    undoManager: undefined,
    serverUpdates: [] as IJsonPatch[]
  }))
  .actions(self => ({
    applyImportedData(snap: SnapshotOut<typeof AppDetails>, id: string) {
      const app = self.app.get(id)
      for (const k in snap) {
        if (k !== 'key' && app[k]) {
          if (snap[k] && typeof snap[k] === 'object') {
            app[k] = { ...getSnapshot(app[k]), ...snap[k] }
          } else {
            app[k] = snap[k]
          }
        }
      }
      return app;
    },
    copyItemToApp (key: string, path: string, item: SnapshotOrInstance<AppItem>) {
      //      let key = `apps:${appKey}`
      if (!self.app.has(key)) {
        withoutPatch(self, () => {
          self.app.put({ key })
        })
      }
      self.app.get(key)[path].put(item)
    },
    /* setCurrentApp(snap) {
     *   withoutPatch(() => {
     *     applySnapshot(self, snap)
     *   })
     * }, */
    newApp (appInfo: SnapshotOut<typeof AppInfo>, snap: SnapshotOut<typeof AppDetails>, id: string) {
      id = id || shortid()
      self.apps.set(id, { ...appInfo, id })
      if (!self.app.has(id)) self.app.set(id, { key: id })
      const app = this.applyImportedData(snap, id);
      if (!app.assignments.size) {
        app.assignments.put({ id: 'default-assignment', name: 'Default', device: 'desktop' })
        app.assignments.put({ id: 'default-mobile-assignment', name: 'Mobile Default', device: 'mobile' })
        app.assignments.put({ id: 'default-tablet-assignment', name: 'Tablet Default', device: 'tablet' })
      }

      return self.apps.get(id)
      //      self.app.set(snap.name, {...snap, key: snap.name })
    },
    async importStack (stack: any, updatedContextFields: Array<any>, id: string) : Promise<void> {
      return new Promise((resolve, reject) => {
        const dbKeyPrefix = getDataSourceDbKeyPrefix(self);
        const updatedSecretFields = updatedContextFields.filter(f => f.type === 'secret');
        const updatedFields = updatedContextFields.filter(f => f.type !== 'secret');
        const stackContext = stack.dataSources[stack.stackContextId];
        const updatedContext = cloneDeep(stackContext);
        
        if (updatedContext.inputs.children) {
          traverseConfigFields(updatedContext.inputs.children, (node, key) => {
            const updatedField = updatedFields.find(f => f.key === key);
  
            if (updatedField) {
              node.value = updatedField.value
            }
          })
        }
  
        const updatedStack = {
          ...stack,
          dataSources: {
            ...stack.dataSources,
            [stack.stackContextId]: updatedContext
          }
        }
  
        const dataSources = Object.values(updatedStack.dataSources).map((ds: Instance<typeof DataSource>) => {
          const dsData = {
            id: ds.id,
            oldDsKey: ds.key,
            newDsKey: dbKeyPrefix + ds.id,
            provider: ds.type,
            targetFieldNames: undefined
          }
  
          const secretFields = getDsSecretFields(ds);
          if (secretFields) {
            dsData.targetFieldNames = secretFields.map(f => f.key);
          }
          
          return dsData;
        });
  
        const replaceKeysGroups = [updatedStack.pages, updatedStack.mapping, updatedStack.dataSources];
        replaceKeysGroups.forEach(group => {
          Object.values(group).forEach((item: Instance<typeof DataSource | typeof MappingItem | typeof Component>) => {
            if (item.key) {
              item.key = item.key.replace(updatedStack.stackId, `app:${id}`);
            }
          })
        })
  
        transaction(() => this.applyImportedData(updatedStack, id))
        
        setTimeout(() => {
          (async() => {
            await save(self.serverUpdates.splice(0));
  
            try {
              await fetch('/admin/stack/import', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  stackContext: {
                    id: updatedStack.stackContextId,
                    updatedFields: updatedSecretFields
                  },
                  dataSources
                })
              })

              resolve();
            } catch (e) {
              console.error(e)
              reject(e);
            }
          })()
        })
      });
      
    }
  }))
  .views(self => ({
    prettyPrintPath (pathOrNode: string | IAnyStateTreeNode) {
      const path = typeof pathOrNode === 'string' ? pathOrNode : getPath(pathOrNode)
      try {
        const pathArray = splitJsonPath(path)
        const result = pathArray.map((v, i) => {
          const pathPart = joinJsonPath(pathArray.slice(0, i+1))
          const node = resolve(self, pathPart)
          if (isStateTreeNode(node)) {
            const t = getType(node)
            if (isModelType(t) && !['AppDetails', 'Store'].includes(t.name)) {
              if (t.name === 'Mapping') return (node as any).key
              if (t.name === 'MappingItem') return (node as any).comp?.name || (node as any).comp?.type
              if (t.name === 'ComponentItem') return (node as IComponent).name || (node as IComponent).type
              return (node as any).name || v
            }
          }
        }).filter(x => x).join(' → ')
        
        return result
      } catch (err) {
        return path
      }
    }
  }))
  .actions(self => {
    const d_save = debounce(() => runInAction(() => {
      self.setSaveProgress(true)
      save(self.serverUpdates.splice(0)).then(() => {
        self.setSaveProgress(false)
        endTimer()
        self.currentApp?.setLoadingProgress(1)
      }).catch(() => {
        self.setSaveProgress(false)
        endTimer()
        self.currentApp?.setLoadingProgress(.999, 'danger')
      })
    }), 1000)
      
    const shouldSkipPatchSave = path => {
      const basePath = splitJsonPath(path).slice(0,4)
      if (basePath.length === 4) {
        const node = resolve(self, joinJsonPath(basePath))
        return node?.meta?.isVolatileNode
      }
    }

    const onPatch = (patches: IJsonPatch[], undoOrRedo: string) => {
      for (const patch of patches) {
        let { path, op, value } = patch

        if (shouldSkipPatchSave(path)) continue;
        
        if(undoOrRedo) {
          const prettyPath = self.prettyPrintPath(path)
          path = changeAppKey(path)
          const parts = path.split(/^\/([^\/]+\/?[^\/]+)/).filter(String)
          path = '/' + parts[0].replace(/:/g, '/')
          const mainNode = resolve(self, path)
          if (!mainNode) continue
          op = 'replace'
          path = changeAppKey(path)
          value = getSnapshot(mainNode)
          errorToast(undoOrRedo, prettyPath, 'success', 2000, undoOrRedo)
          
        }
        
        // TODO: move to transformPatches
        if (!/\d+$/.test(path) && op === 'replace') {
          //cut out patches for the same path and op
          const existingIndex = self.serverUpdates.findIndex(u => u.op === 'replace' && u.path === path)
          if (existingIndex >= 0) {
            self.serverUpdates.splice(existingIndex, 1)
          }
          //if patch is { op:replace, value: undefined, path: /a/b }, cut out all previous /a/b, /a/b/c, /a/b/c/d...
          if (!value) {
            self.serverUpdates = self.serverUpdates.filter(item => !item.path.includes(path));
          }
        }
        self.serverUpdates.push({ path, op, value })
      }
      d_save()
      startTimer()
    }
    
    let start, reqId
    let max = 1200
    const step = (ts) => {
      if(!start) {
        start = ts
      }
      const elapsed = ts - start

      if(elapsed < max) {
        self.currentApp?.setLoadingProgress(elapsed / max, 'success')
        reqId = window.requestAnimationFrame(step)
      } else start = undefined
    }

    const startTimer = (rotationSeconds = 1200) => {
      max = rotationSeconds
      endTimer()
      reqId = window.requestAnimationFrame(step)
    }

    const endTimer = () => {
      window.cancelAnimationFrame(reqId)
      reqId = undefined
      start = undefined
    }
    
    return {
      onPatch: (patches) => onPatch(patches, undefined),
      onUndo: (patches) => onPatch(patches, 'undo'),
      onRedo: (patches) => onPatch(patches, 'redo'),
      resetUndoManager (undoManager) {
        if (undoManager) self.undoManager = undoManager
        else if (self.undoManager) self.undoManager.reset()
      },
      forceSaveChanges: flow(function* () {
        yield save(self.serverUpdates.splice(0))
      }),
      startWheelTimer: (rotationSeconds) => startTimer(rotationSeconds)
    }
  })
  .actions(crudActions)
  .views(self => ({
    getAllItemsAtUrl (location: string) {
      const paths = location.split('/').filter(p => p && p !== '')
      let comp: SnapshotIn<AppItem>
      return paths
        .map((path, index) => {
          if (index === 0) {
            comp = { name: path, path, components: self[path] }
          } else if (comp.components && comp.components.length > 0) {
            comp = comp.components[parseInt(path)]
          }
          return comp
        })
        .filter(arr => (item: SnapshotIn<AppItem>, index: number, array: SnapshotIn<AppItem>[]) => array.indexOf(item) === index)
    },
    getOrLoadObject (name: string, key: string) {
      return self.currentApp?.getOrLoadObject(name, key)
    },
    getItemAtUrl (pathname: string, url: string[]) {
      return self.currentApp?.getItemAtUrl && self.currentApp.getItemAtUrl(pathname, url)
    },
    get user () {
      const env = getEnv(self)
      return env && env.user
    }
  }))
  .actions(self => ({
    newMapping (m = {}) {
      return self.currentApp.newMapping(m)
    },
    newComponent (path: string, comp: SnapshotOrInstance<typeof Component> = { name: 'New' }, cmpType: string) {
      return self.currentApp.newComponent(path, comp, cmpType)
    },
    setCurrentAssignmentVersion (ver: Instance<typeof AssignmentVersion>, device: string) {
      const tmp = cloneDeep(getSnapshot(self).currentAssignmentVersions)
      _set(tmp, `${ver.group}.${device}`, ver.id)
      _set(tmp, `${ver.group}.appId`, self.currentAppKey)
      _set(tmp, `${ver.group}.${device}AssignmentName`, self.currentApp?.assignment?.name)
      _set(tmp, `${ver.group}.${device}AssignmentDescription`, self.currentApp?.assignment?.description)
      self.currentAssignmentVersions = tmp
    },
    newAssignmentVersion (ver = { name: 'New' }) {
      const id = shortid()
      self.currentApp?.assignmentVersions.put({
        ...ver,
        id,
        shortId: id
      })
      return self.currentApp?.assignmentVersions.get(id)
    },
    newDataSource ({ type }, testId?: string) {
      const id = testId || uuid()
      const newDS = {
        type,
        name: `${type} ${self.dataSources.size}`,
        id,
        tableName: `${type}_${self.dataSources.size}`
      }

      self.currentApp?.dataSources.put(newDS)
      return self.dataSources.get(id)
    },
    setSetting (key: string, val: any) {
      self.settings = { ...self.settings, [key]: val }
    },
    newSite (site = { name: 'New' }) {
      const id = alphaNumId()
      self.sites.put({
        ...site,
        id
      })
      return self.sites.get(id)
    }
  }))
  .actions(self => ({
    exportComponent (component: IComponent) {
      const existing = Array.from(self.components.values()).find(
        c => c.name === component.name
      )
      const cmp = component.getSnapshotWithNewIds()

      if (existing) {
        existing.del()
      }
      self.newComponent('components', cmp, cmp.type)
    }
  }))
  .views(self => {

    function getComponentLabels (comp: IComponent, arr = []) {
      arr.push(comp.labels())
      comp.components.map((c: IComponent): string[] => getComponentLabels(c, arr))
      return arr
    }

    return {
      tree (type: 'data' | 'function') {
	return self.currentApp?.tree(type)
      },
      getComponentLabels,
      componentTree (item: IComponent) {
	if (item && item.mainParent) {
          return getComponentLabels(item.mainParent).map((c: IComponent) => ({
            ...c,
            type: 'component'
          }))
	}
	return []
      }
    }
  })
  .views(self => ({
    get dataTree () {
      return self.currentApp?.tree('data')
    },
    get functionTree () {
      return self.currentApp?.tree('function')
    }
  }))

export type IStore = Instance<typeof Store>

export let localDev = process.env.LOCAL_DEV === 'true'

export let store: IStore = null

let localState = null

export const save = async (updates: IJsonPatch[]) => {
  if(localDev) return
  if (!updates || !updates.length) return
  return new Promise((resolve, reject) => {
    fetch('/admin/save', {
      credentials: 'include',
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(
        cleanDeep(toJS(transformPatches(updates, store)), {
          emptyArrays: false,
          emptyObjects: false
        }).map(u => ({ ...u, path: changeAppKey(u.path) }))
      )
    })
      .then(res => {
        if (!res.ok) {
          return res.json().then(err => {
            throw err
          })
        }
        return res.json()
      })
      .catch(er => {
        errorToast('Error saving changes. Please refresh.', er)
        reject(er)
      })
      .then(resolve)
  })
}

export const initStore = (
  state: IAnyStateTreeNode = {},
  environment: any,
  isServer = false,
  isLocalDev = false
) => {
  if (state?.$treenode) return state
  isLocalDev = isLocalDev && typeof window !== 'undefined'
  if (isLocalDev) {
    if(new URLSearchParams(location.search).has('reloadStore')) {
      localStore.set('polystack@local', cleanDeep(state, { emptyArrays: false, emptyObjects: false }))
    } 
    localState = localStore.get('polystack@local')
  }

  if (isServer === true) return Store.create({}, environment)

  if (!store) {
    if (isLocalDev) {
      store = Store.create({ ...localState }, environment)
    } else store = Store.create({ ...state }, environment)

    if (isLocalDev) {
      onSnapshot(
        store,
        debounce(snapshot => {
          localStore.set(
            'polystack@local',
            cleanDeep(snapshot, {
              emptyArrays: false,
              emptyObjects: false
            })
          )
        }, 1000)
      )
    }

    if (typeof window !== 'undefined') {
      (window as any).store = store
    }
  }

  return store
}
